import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TenantBrand } from "@/components/TenantBrand";
import { Brand } from "@/components/Brand";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { AIChat } from "@/components/AIChat";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Package, Cog, ArrowLeftRight, ShoppingCart, Ship, ClipboardList,
  CalendarClock, Truck, MapPin, Bell, Users as UsersIcon, Settings as SettingsIcon,
  Menu, LogOut, Smartphone, ScanLine, Wrench, BarChart3, KeyRound, UserRound, Building2, FileText, Receipt, ScanSearch,
} from "lucide-react";

const MENU = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/pecas", label: "Peças", icon: Package, module: "estoque" },
  { to: "/maquinas", label: "Máquinas", icon: Cog, module: "maquinas" },
  { to: "/movimentacoes", label: "Movimentações", icon: ArrowLeftRight, module: "movimentacoes" },
  { to: "/entrada-nfe", label: "Entrada por NF-e", icon: Receipt, roles: ["ALMOXARIFADO"], module: "movimentacoes" },
  { to: "/manutencao-pecas", label: "Pendências Manutenção", icon: Wrench, module: "manutencao" },
  { to: "/relatorios/pecas-usadas", label: "Relatório de Peças Usadas", icon: FileText, module: "manutencao" },
  { to: "/curva-abc", label: "Curva ABC", icon: BarChart3, module: "curva_abc" },
  { to: "/compras", label: "Compras Recomendadas", icon: ShoppingCart, module: "compras" },
  { to: "/importacoes", label: "Importações", icon: Ship, module: "importacoes" },
  { to: "/inventario", label: "Inventário", icon: ClipboardList, module: "inventario" },
  { to: "/auditoria-pecas", label: "Auditoria de Peças", icon: ScanSearch, roles: ["ALMOXARIFADO", "COMPRAS", "CONSULTA"], module: "estoque" },
  { to: "/maquinas-planejadas", label: "Máquinas Planejadas", icon: CalendarClock, module: "planejamento" },
  { to: "/fornecedores", label: "Fornecedores", icon: Truck, module: "cadastros" },
  { to: "/localizacoes", label: "Localizações", icon: MapPin, module: "cadastros" },
  { to: "/colaboradores", label: "Colaboradores", icon: UserRound, roles: ["ALMOXARIFADO"], module: "colaboradores" },
  { to: "/alertas", label: "Alertas", icon: Bell, module: "alertas" },
  { to: "/usuarios", label: "Usuários", icon: UsersIcon, admin: true },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, admin: true },
];

function NavItems({ onNavigate }) {
  const { user, hasModule } = useAuth();
  const isAdmin = user?.role === "ADMINISTRADOR";
  return (
    <nav className="flex-1 space-y-1 px-3 py-2 overflow-y-auto">
      {MENU.filter((m) => {
        if (m.module && !hasModule(m.module)) return false;
        if (m.admin) return isAdmin;
        if (m.roles) return isAdmin || m.roles.includes(user?.role);
        return true;
      }).map((m) => (
        <NavLink
          key={m.to}
          to={m.to}
          end={m.to === "/"}
          onClick={onNavigate}
          data-testid={`nav-${m.to === "/" ? "dashboard" : m.to.slice(1)}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-sky-800/40 text-sky-100 border border-sky-700/50"
                : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border border-transparent"
            }`
          }
        >
          <m.icon className="h-[18px] w-[18px] shrink-0" />
          <span>{m.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function Layout() {
  const { user, logout, hasModule } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const Sidebar = (
    <div className="flex h-full flex-col">
      <div className="px-4 py-5 border-b border-slate-800">
        {user?.is_platform_admin ? <Brand /> : <TenantBrand user={user} />}
      </div>
      <NavItems onNavigate={() => setOpen(false)} />
      <div className="p-3 border-t border-slate-800 space-y-2">
        {user?.is_platform_admin && (
          <Button
            data-testid="btn-platform-admin"
            variant="secondary"
            className="w-full justify-start gap-2 bg-violet-900/30 hover:bg-violet-800/40 text-violet-100 border border-violet-800/40"
            onClick={() => navigate("/plataforma/empresas")}
          >
            <Building2 className="h-4 w-4" /> Administração da Plataforma
          </Button>
        )}
        {(hasModule("estoque") || hasModule("manutencao") || hasModule("inventario")) && (
          <Button
            data-testid="btn-modo-operacao"
            variant="secondary"
            className="w-full justify-start gap-2 bg-sky-900/40 hover:bg-sky-800/50 text-sky-100"
            onClick={() => navigate("/operacional")}
          >
            <Smartphone className="h-4 w-4" /> Modo Operação (Tablet)
          </Button>
        )}
        <div className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-100 truncate">{user?.name}</div>
            <div className="text-[11px] text-sky-400 font-mono uppercase">{user?.role_label}</div>
          </div>
          <div className="flex items-center">
            <Button data-testid="btn-change-password" size="icon" variant="ghost" onClick={() => setPwdOpen(true)} title="Alterar senha" className="text-slate-400 hover:text-sky-300">
              <KeyRound className="h-4 w-4" />
            </Button>
            <Button data-testid="btn-logout" size="icon" variant="ghost" onClick={logout} className="text-slate-400 hover:text-rose-300">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen almox-bg text-slate-100">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col glass border-r border-slate-800 z-30">
        {Sidebar}
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-800 glass px-4 py-3 lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="btn-menu-mobile" size="icon" variant="ghost"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-slate-950 border-slate-800">
              {Sidebar}
            </SheetContent>
          </Sheet>
          {user?.is_platform_admin ? <Brand size="sm" /> : <TenantBrand user={user} size="sm" />}
          {hasModule("estoque") && (
            <Button size="icon" variant="ghost" className="ml-auto" onClick={() => navigate("/operacional/scan")}>
              <ScanLine className="h-5 w-5 text-sky-400" />
            </Button>
          )}
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      {hasModule("ia") && <AIChat />}
    </div>
  );
}
